import React, { useMemo, useState } from "react";
import { Radio, DatePicker, Space, Button } from "antd";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import CustomButton from "components/Common/ui/CustomButton";
import { LocalizedRangePicker } from "components/Common/ui/LocalizedDatePicker";

const { RangePicker } = DatePicker;

type FinancePeriod = "last_week" | "last_month" | "year" | "custom" | "this_year" | "this_day" | "this_week" ;

type Props = {
    loading?: boolean;
    initialPeriod?: Exclude<FinancePeriod, "custom">; // default "year"
    onChange: (params: { period?: string; start?: string; end?: string }) => void; // send to API
    onReset: () => void;
};

export default function FinanceDashboardFilter({
    loading,
    initialPeriod = "this_year",
    onChange,
    onReset,
}: Props) {
    const { t } = useTranslation();
    const [period, setPeriod] = useState<FinancePeriod>(initialPeriod);
    const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

    const presets = useMemo(
        () => [
            { label: t("last_7_days"), value: [dayjs().add(-6, "day").startOf("day"), dayjs().endOf("day")] as [Dayjs, Dayjs] },
            { label: t("last_30_days"), value: [dayjs().add(-29, "day").startOf("day"), dayjs().endOf("day")] as [Dayjs, Dayjs] },
            { label: t("this_month"), value: [dayjs().startOf("month"), dayjs().endOf("day")] as [Dayjs, Dayjs] },
            { label: t("last_month"), value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] as [Dayjs, Dayjs] },
            { label: t("this_year"), value: [dayjs().startOf("year"), dayjs().endOf("day")] as [Dayjs, Dayjs] },
        ],
        [t]
    );

    const handlePeriodChange = (e: any) => {
        const next = e.target.value as FinancePeriod;
        setPeriod(next);

        if (next !== "custom") {
            // hide/clear range and auto-apply preset
            setRange(null);
            onChange({ period: next });
        }
    };

    const handleApplyCustom = () => {
        if (!range || !range[0] || !range[1]) return;
        const start = range[0].startOf("day").toDate().toISOString();
        const end = range[1].endOf("day").toDate().toISOString();
        onChange({ start, end });
    };

    const handleReset = () => {
        setPeriod(initialPeriod);
        setRange(null);
        onReset();
    };

    return (
        <div className="card mb-3">
            <div className="card-body d-flex flex-wrap gap-2 align-items-center">
                {/* Presets selector (radio buttons look like a segmented control, but no CSS bugs) */}
                <Radio.Group
                    value={period}
                    onChange={handlePeriodChange}
                    disabled={!!loading}
                    optionType="button"
                    buttonStyle="solid"
                >
                    <Radio.Button value="this_day">{t("today")}</Radio.Button>
                    {/* <Radio.Button value="this_week">{t("this_week")}</Radio.Button> */}
                    {/* <Radio.Button value="this_month">{t("this_month")}</Radio.Button> */}

                    {/* <Radio.Button value="this_year">{t("this_year")}</Radio.Button> */}
                    <Radio.Button value="last_week">{t("last_week")}</Radio.Button>
                    <Radio.Button value="last_month">{t("last_month")}</Radio.Button>
                    <Radio.Button value="this_year">{t("this_year")}</Radio.Button>
                    <Radio.Button value="custom">{t("custom")}</Radio.Button>
                </Radio.Group>

                {/* Custom range appears only when "custom" is selected */}
                {period === "custom" && (
                    <Space size="small" wrap>
                        <LocalizedRangePicker
                            value={range as any}
                            onChange={(vals) => setRange(vals as [Dayjs | null, Dayjs | null] | null)}
                            allowClear
                            inputReadOnly
                            disabled={!!loading}
                            presets={presets}
                        />

                        <CustomButton
                            onClick={handleApplyCustom}
                            disabled={!range?.[0] || !range?.[1] || !!loading}
                        >
                            {t("apply")}
                        </CustomButton>
                        <CustomButton
                            onClick={handleReset}
                            variant="outline-primary"
                            disabled={!!loading}
                        >
                            {t("reset")}
                        </CustomButton>
                    </Space>
                )}

                {/* If not custom, still show Reset on the right */}
                {period !== "custom" && (
                    <CustomButton onClick={handleReset} disabled={!!loading} variant="outline-primary">
                        {t("reset")}
                    </CustomButton>
                )}
            </div>
        </div>
    );
}
